/**
 * State hashing — two FNV-1a lanes over a canonical byte stream.
 *
 * The hash is a curated walk of `SimState`, not a blanket serialisation: what
 * it covers is what "the same run" means. Bookkeeping that must never move a
 * replay (doc 08's stats) is deliberately outside it, and adding a field to the
 * state does not silently invalidate every blessed golden.
 */

import { RES_IDS } from './defs.ts';
import type { SimState } from './types.ts';

class Hasher {
  private a = 0x811c9dc5;
  private b = 0xcbf29ce4;
  private readonly buf = new DataView(new ArrayBuffer(8));

  private byte(v: number): void {
    this.a = Math.imul(this.a ^ v, 0x01000193) >>> 0;
    this.b = Math.imul(this.b ^ v, 0x01000197) >>> 0;
  }

  i32(v: number): this {
    this.buf.setInt32(0, v | 0);
    for (let k = 0; k < 4; k++) this.byte(this.buf.getUint8(k));
    return this;
  }

  f64(v: number): this {
    this.buf.setFloat64(0, v);
    for (let k = 0; k < 8; k++) this.byte(this.buf.getUint8(k));
    return this;
  }

  str(s: string): this {
    this.i32(s.length);
    for (let k = 0; k < s.length; k++) this.byte(s.charCodeAt(k) & 0xff);
    return this;
  }

  hex(): string {
    return this.a.toString(16).padStart(8, '0') + this.b.toString(16).padStart(8, '0');
  }
}

/** Two-lane FNV-1a over the full state, as 16 hex chars. */
export function hashState(s: SimState): string {
  const h = new Hasher();
  h.i32(s.tick).i32(s.rngState).str(s.outcome);
  for (const r of RES_IDS) h.f64(s.resources[r]);
  h.i32(s.nextId).i32(s.fightsCleared).i32(s.pullsMade);
  h.f64(s.capacitor).i32(s.act).i32(s.lastPullTick);
  for (const a of ['q', 'w', 'e', 'r'] as const) h.i32(s.abilityReady[a]);
  for (const [key, left] of Object.entries(s.deposits)) h.str(key).f64(left);
  for (const st of s.structures) {
    h.i32(st.id).str(st.def).i32(st.i).i32(st.j).f64(st.hp);
    h.i32(st.ruin ? 1 : 0).i32(st.ruinWavesLeft).f64(st.cooldown).i32(st.targetId);
    h.i32(st.facing).i32(st.firedAt);
    h.i32(st.upgrades.a * 100 + st.upgrades.b * 10 + st.upgrades.c).i32(st.disabledUntil);
    h.f64(st.ramp).f64(st.healRate).i32(st.healUntil);
    for (const t of st.depositTiles) h.i32(t);
  }
  for (const e of s.enemies) {
    h.i32(e.id).str(e.species).f64(e.x).f64(e.y).f64(e.hp).f64(e.maxHp);
    h.i32(e.attackTargetId).i32(e.waveId).f64(e.laneX).f64(e.laneY);
    h.f64(e.slowPct).i32(e.slowUntil).i32(e.stunUntil);
    h.f64(e.bleedDps).i32(e.bleedUntil).f64(e.bleedSlowPct).f64(e.bleedSpreadRadius);
    h.f64(e.burnDps).i32(e.burnUntil).i32(e.gimmickTick).i32(e.goalStructId);
    h.i32(e.revealedUntil).i32(e.litUntil).f64(e.litAmp).i32(e.aoeUntil).f64(e.aoeAmp);
    h.f64(e.armorShredPct).i32(e.armorShredUntil).i32(e.divingUntil).i32(e.biteTick);
    h.i32(e.regenPauseUntil).i32(e.gimmickTick2);
  }
  for (const p of s.projectiles) {
    h.i32(p.id).f64(p.x).f64(p.y).i32(p.landTick).f64(p.damage).f64(p.radius);
    h.i32(p.bombletCount).f64(p.knockback);
  }
  for (const r of s.rim) {
    h.str(r.id).f64(r.orbit).i32(r.patience).i32(r.riled ? 1 : 0).str(r.state);
  }
  for (const w of s.waves) {
    h.i32(w.id).str(w.shoalId).i32(w.startTick).str(w.edge).i32(w.pullIndex);
    h.i32((w.doubleReel ? 1 : 0) + (w.cleared ? 2 : 0));
    for (const rid of w.roster) h.i32(rid);
  }
  for (const p of s.pendingSpawns) {
    h.i32(p.tick).str(p.species).i32(p.waveId).str(p.edge).f64(p.along);
    h.f64(p.atX ?? -1).f64(p.atY ?? -1);
  }
  return h.hex();
}
